<template>
  <div
    class="appointment-report ar-question"
    :class="{
      'ar-question--compact': compact,
      'ar-question--invalid': showError,
      'ar-question--disabled': disabled,
      'ar-anim-ctl-in': rowEnterFx,
    }"
    :style="rowEnterFx ? delayStyle : null"
  >
    <!-- Aguardando horário: pergunta legível, controle substituído por um marcador tracejado com cadeado. -->
    <div
      v-if="locked"
      class="ar-question__row"
    >
      <p class="ar-question__locked-label">
        <feather-icon
          icon="LockIcon"
          size="16"
          class="ar-question__locked-icon"
          aria-hidden="true"
        />
        <span>{{ legend }}</span>
      </p>
      <span
        class="ar-question__placeholder"
        aria-hidden="true"
      >
        <feather-icon
          icon="LockIcon"
          size="14"
        />
      </span>
    </div>

    <fieldset
      v-else
      class="ar-question__row"
    >
      <legend class="ar-question__legend">
        <span
          class="ar-question__glyph"
          aria-hidden="true"
        >
          <span
            v-if="!isComplete"
            class="ar-question__ring"
          />
          <span
            v-else
            class="ar-question__done"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline
                :class="{ 'ar-draw-check': touched }"
                points="20 6 9 17 4 12"
              />
            </svg>
          </span>
        </span>
        <span class="ar-question__label">{{ legend }}</span>
      </legend>

      <div
        class="ar-question__control"
        :class="{ 'ar-anim-ctl-in': enterFx }"
        :style="enterFx ? delayStyle : null"
      >
        <div
          class="ar-seg"
          :class="{
            'ar-seg--invalid': showError,
            'ar-seg--inert': isInert,
          }"
        >
          <span
            v-if="hasValue"
            class="ar-seg__thumb"
            :class="{ 'ar-seg__thumb--second': value === options[1].value }"
            aria-hidden="true"
          >
            <span
              class="ar-seg__thumb-fill"
              :class="{ 'ar-seg__thumb-fill--enter': thumbEntering }"
            />
          </span>
          <label
            v-for="option in options"
            :key="String(option.value)"
            class="ar-seg__option"
          >
            <input
              :id="inputId(option)"
              ref="inputs"
              type="radio"
              class="ar-sr-only"
              :name="groupName"
              :value="String(option.value)"
              :checked="value === option.value"
              :disabled="isInert"
              :aria-describedby="showError ? errorId : null"
              @change="select(option.value)"
            >
            <span
              class="ar-seg__text"
              :class="{ 'ar-seg__text--checked': value === option.value }"
            >
              <feather-icon
                v-if="value === option.value"
                icon="CheckIcon"
                size="16"
                class="ar-seg__check"
                aria-hidden="true"
              />
              {{ option.label }}
            </span>
          </label>
        </div>
        <span
          v-if="showError"
          :id="errorId"
          class="ar-question__error ar-anim-fade-in"
        >{{ resolvedErrorMessage }}</span>
      </div>
    </fieldset>

    <!-- Pergunta/select que esta resposta libera (reveal de 260ms com a linha de ramificação). -->
    <transition name="ar-reveal">
      <div
        v-if="followUpVisible && !locked"
        class="ar-question__follow"
      >
        <div class="ar-question__follow-inner">
          <span
            class="ar-question__branch"
            aria-hidden="true"
          />
          <slot name="follow-up" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { NEW_COPY, YES_NO_OPTIONS } from '@/constants/appointment_report_v2'
import { nextReportUid } from '../helpers/report_ui'

/**
 * Pergunta Sim | Não do Report (spec-v3 §2.A/C): `<fieldset>` + `<legend>` + dois rádios reais escondidos —
 * teclado nativo do grupo (Tab entra, setas trocam), Sim sempre à esquerda, nenhum valor padrão.
 *
 *   <report-yes-no
 *     v-model="answers.had_meeting"            (true | false | null)
 *     legend="A Reunião foi realizada?"
 *     :invalid="attempted && answers.had_meeting === null"
 *     :complete="respondida e o motivo (se houver) também"
 *     :follow-up-visible="answers.had_meeting === false"
 *   >
 *     <template #follow-up><report-reason-select … /></template>
 *   </report-yes-no>
 *
 * `focus()` leva o foco à opção marcada (ou a "Sim") — usado pelo "ir para a pergunta que falta".
 */
export default {
  name: 'ReportYesNo',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: null,
    },
    legend: {
      type: String,
      required: true,
    },
    // Erro visível (normalmente: tentou finalizar e esta pergunta está sem resposta).
    invalid: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    // Estado do glyph de status. null = "respondida"; passe false enquanto o motivo liberado estiver vazio.
    complete: {
      type: Boolean,
      default: null,
    },
    // Não se aplica / indisponível: esmaece e bloqueia.
    disabled: {
      type: Boolean,
      default: false,
    },
    // Bloqueia sem mudar a aparência (ex.: enquanto finaliza).
    readonly: {
      type: Boolean,
      default: false,
    },
    // Prévia inerte do "aguardando horário".
    locked: {
      type: Boolean,
      default: false,
    },
    followUpVisible: {
      type: Boolean,
      default: false,
    },
    // Linha mais baixa (painel da central).
    compact: {
      type: Boolean,
      default: false,
    },
    // Formulário liberado no horário: o controle entra (fade + 4px, 200ms) com `enterDelay` (30ms por linha).
    enterFx: {
      type: Boolean,
      default: false,
    },
    // A linha inteira volta a aparecer (a 1ª pergunta passou de Não para Sim).
    rowEnterFx: {
      type: Boolean,
      default: false,
    },
    enterDelay: {
      type: Number,
      default: 0,
    },
    name: {
      type: String,
      default: '',
    },
    // Prefixo dos ids: `${idPrefix}-yes` / `${idPrefix}-no` / `${idPrefix}-error`.
    idPrefix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fallbackUid: nextReportUid('ar-yn'),
      // Só anima (thumb e check) depois de uma escolha do usuário — respostas restauradas do rascunho entram paradas.
      touched: false,
      thumbEntering: false,
    }
  },
  computed: {
    uid() {
      return this.idPrefix || this.fallbackUid
    },
    options() {
      return YES_NO_OPTIONS
    },
    hasValue() {
      return this.value === true || this.value === false
    },
    isComplete() {
      return this.complete === null ? this.hasValue : this.complete
    },
    isInert() {
      return this.disabled || this.readonly
    },
    showError() {
      return this.invalid && !this.locked
    },
    groupName() {
      return this.name || `${this.uid}-group`
    },
    errorId() {
      return `${this.uid}-error`
    },
    resolvedErrorMessage() {
      return this.errorMessage || NEW_COPY.answerRequired
    },
    delayStyle() {
      return { '--ar-delay': `${Math.max(0, this.enterDelay)}ms` }
    },
  },
  methods: {
    inputId(option) {
      return `${this.uid}-${option.value ? 'yes' : 'no'}`
    },
    select(value) {
      if (this.isInert || value === this.value) return
      // 1ª escolha: o thumb não desliza, surge (scale .96→1). Depois, desliza.
      this.thumbEntering = !this.hasValue
      this.touched = true
      this.$emit('input', value)
      this.$emit('change', value)
    },
    focus(options) {
      const inputs = this.$refs.inputs || []
      const index = this.value === this.options[1].value ? 1 : 0
      const target = inputs[index] || inputs[0]
      if (target) target.focus(options)
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-question__row {
  min-width: 0;
  min-height: 72px;
  margin: 0;
  padding: 16px 0;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 0;
}

// Linhas do cartão de perguntas separadas por 1px (a primeira sem divisória).
.ar-question + .ar-question {
  border-top: 1px solid var(--ar-divider);
}

.ar-question--compact .ar-question__row {
  min-height: 0;
}

// `float` tira a legend do layout especial de <fieldset> e a torna um item flex comum.
.ar-question__legend {
  float: left;
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-question__glyph {
  position: relative;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 3px;
}

.ar-question__ring {
  position: absolute;
  inset: 0;
  border-radius: var(--ar-radius-pill);
  border: 1.5px solid var(--ar-text-placeholder);
  transition: border-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.ar-question--invalid .ar-question__ring {
  border-color: var(--ar-warning);
}

.ar-question__done {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-selected-tint);
  color: var(--ar-brand-text);
}

.ar-question--compact .ar-question__done svg {
  width: 10px;
  height: 10px;
}

.ar-question__label {
  min-width: 0;
}

.ar-question__control {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

// ---- Segmented Sim | Não (200×40, trilho #1E2640, thumb que desliza 200ms) ----
.ar-seg {
  position: relative;
  width: 200px;
  height: 40px;
  padding: 2px;
  display: flex;
  border: 1px solid var(--ar-control);
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  transition: border-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.ar-seg--invalid {
  border-color: var(--ar-warning);
}

.ar-seg__thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  width: calc(50% - 2px);
  pointer-events: none;
  transition: transform var(--ar-dur-thumb) var(--ar-ease-standard);
}

.ar-seg__thumb--second {
  transform: translateX(100%);
}

.ar-seg__thumb-fill {
  position: absolute;
  inset: 0;
  border-radius: var(--ar-radius-thumb);
  background: var(--ar-selected-tint);
  box-shadow: inset 0 0 0 1.5px var(--ar-brand-text);
}

.ar-seg__thumb-fill--enter {
  animation: ar-thumb-in var(--ar-dur-fast) var(--ar-ease-standard) both;
}

.ar-seg__option {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  display: flex;
  cursor: pointer;
}

.ar-seg__text {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: var(--ar-radius-thumb);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
  transition:
    color var(--ar-dur-hover) var(--ar-ease-standard),
    background-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.ar-seg__text--checked {
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-seg__check {
  flex-shrink: 0;
  color: var(--ar-brand-text);
}

.ar-seg:not(.ar-seg--inert) .ar-seg__option:hover .ar-seg__text:not(.ar-seg__text--checked) {
  background: var(--ar-bg-raised);
}

.ar-seg input:focus-visible + .ar-seg__text {
  outline: 2px solid var(--ar-focus);
  outline-offset: 2px;
}

.ar-seg--inert .ar-seg__option {
  cursor: default;
}

.ar-question--disabled {
  .ar-question__legend,
  .ar-seg {
    opacity: .56;
  }
}

.ar-question__error {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-warning);
  white-space: nowrap;
}

// ---- Prévia bloqueada (aguardando horário) ----
.ar-question__locked-label {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: var(--ar-text-2);
}

.ar-question__locked-icon {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--ar-text-muted);
}

.ar-question__placeholder {
  flex-shrink: 0;
  width: 200px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--ar-control);
  border-radius: var(--ar-radius-control);
  color: var(--ar-text-muted);
}

// ---- Follow-up (select liberado pela resposta) ----
.ar-question__follow-inner {
  position: relative;
  margin: 0 0 20px 7px;
  padding: 0 0 0 21px;
}

.ar-question--compact .ar-question__follow-inner {
  margin-bottom: 16px;
}

.ar-question--compact .ar-question__branch {
  top: 0;
}

.ar-question__branch {
  position: absolute;
  top: -12px;
  bottom: 0;
  left: 0;
  width: 2px;
  border-radius: 2px;
  background: var(--ar-branch);
  transform-origin: top center;
}

.ar-reveal-enter-active .ar-question__branch {
  animation: ar-branch-grow var(--ar-dur-reveal) var(--ar-ease-emphasized) both;
}

// ---- Telas estreitas: legenda em cima, controle em largura total (48px de altura) ----
@media (max-width: 575.98px) {
  .ar-question__row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .ar-question__control {
    align-items: stretch;
  }

  .ar-seg,
  .ar-question__placeholder {
    width: 100%;
    height: 48px;
  }

  .ar-question__error {
    white-space: normal;
  }
}
</style>
